import { CSSProperties, useCallback, useEffect, useState } from "react";
import CompanyService, {
  Entities
} from "../../../../../services/CompanyService";
import FieldConfigurationService, {
  AdditionalDefinitionsListResponse
} from "../../../../../services/FieldConfigurationService";
import { TranslationService } from "../../../../../services/TranslationService";
import { ToastService } from "../../../../shared/bootstrap/Toast";
import ErrorMessage from "../../../../shared/components/Error";
import Loading from "../../../../shared/components/Loading";
import FloatingPanelService from "../../../../shared/FloatingPanel";
import AdditionalDefinitionsEdit from "../AdditionalDefinitionsEdit";
import OrderableTable from "../OrderableTable";
import { CompanyAuth } from "../../../../../entities/login/CompanyAuth";
import { Switch } from "../../../../../utils/Utils";
import { EntityFieldItem } from "../EntityFieldItem";
import SearchInput from "../../../../shared/components/SearchInput";
import { ButtonTooltipIcon } from "../../../../shared/components/ButtonTooltipIcon";

export type FieldType =
  | CompanyAuth.ConfigItem
  | AdditionalDefinitionsListResponse.Item;
const EntityFieldConfiguration = ({
  entity,
  titleFieldSection
}: {
  entity: Entities;
  titleFieldSection: string;
}) => {
  const { translate } = TranslationService;
  const [valueSearch, setValueSearch] = useState("");
  const [response, setResponse] = useState<FieldType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const requestData = async () => {
    setError(false);
    const result =
      await FieldConfigurationService.getAdditionalDefinitionList();
    if (result instanceof Error) {
      ToastService.showToast(
        TranslationService.translate.ErrorProcessingRequest,
        undefined,
        "danger"
      );
      setError(true);
      setLoading(false);
      return;
    }
    const fields = [
      ...CompanyService.getConfigItems().filter(
        (x) => x.Entity === entity && x.field !== -27 && x.field !== -28
      ),
      ...result.list.filter((x) => x.Entity === entity)
    ];
    const getValue = <T,>(
      field: FieldType,
      configExtractor: (x: CompanyAuth.ConfigItem) => T,
      additionalExtractor: (x: AdditionalDefinitionsListResponse.Item) => T
    ) =>
      "AdditionalDefinitionID" in field
        ? additionalExtractor(field)
        : configExtractor(field);
    const fieldsOrder =
      Switch(
        [entity === Entities.Client, CompanyService.getPersonSortedFields],
        [entity === Entities.Invoice, CompanyService.getIoSortedFields],
        [entity === Entities.Payment, ""]
      ) ?? "";
    const onlySorted = fieldsOrder
      .split(",")
      .map(
        (x) =>
          fields.find(
            (y) =>
              getValue(
                y,
                (x) => x.field,
                (x) => x.AdditionalDefinitionID
              ).toString() === x
          )!
      )
      .filter((x) => x !== undefined);
    const sorted = [
      ...onlySorted,
      ...fields.filter((x) => !onlySorted.includes(x))
    ];
    if (entity === Entities.Payment) {
      sorted.unshift(
        ...[
          TranslationService.translate.Date,
          TranslationService.translate.PaymentType,
          TranslationService.translate.Amount,
          TranslationService.translate.Observations
        ].map((x, i) => ({
          field: i,
          name: x,
          value: null,
          IncludeInList: true,
          IncludeInMail: true,
          ShowAlways: true,
          IncludeInClientExport: true,
          Entity: entity,
          ManualEdit: true,
          IncludeMainCurrency: true,
          ShowDueDays: true,
          ExcludeInFilters: true,
          Whatsapp: true,
          IncludeInCustomerPortal: true,
          whatsapp: true
        }))
      );
    }
    setResponse(sorted);
    setLoading(false);
    setError(false);
  };
  const requestDataCallback = useCallback(requestData, [entity]);

  useEffect(() => {
    requestDataCallback();
  }, [requestDataCallback]);

  const showAdditionalDefinitionsEditModal = () => {
    FloatingPanelService.showPanel({
      children: (
        <AdditionalDefinitionsEdit
          reload={requestDataCallback}
          entityId={entity}
        />
      ),
      title: titleFieldSection,
      width: 700,
      height: 800,
      position: "bottom-right"
    });
  };

  const onSort = (items: FieldType[]) => {
    const getValue = <T,>(
      field: FieldType,
      configExtractor: (x: CompanyAuth.ConfigItem) => T,
      additionalExtractor: (x: AdditionalDefinitionsListResponse.Item) => T
    ) =>
      "AdditionalDefinitionID" in field
        ? additionalExtractor(field)
        : configExtractor(field);
    const ids = items.map((y) =>
      getValue(
        y,
        (x) => x.field,
        (x) => x.AdditionalDefinitionID
      )
    );
    FieldConfigurationService.setFieldsOrder(ids, entity);
  };

  const headers: { title: string; style?: undefined | CSSProperties }[] = [
    { title: translate.Field },
    { title: translate.Format }
  ];

  if (entity !== Entities.Client) {
    headers.splice(3, 1);
    if (entity === Entities.Payment) {
      headers.splice(2, 1);
    }
  }

  if (entity === Entities.Client) {
    headers.push({ title: translate.CustomerSite });
  }
  headers.push({ title: "", style: { width: "auto" } });
  headers.push({ title: "", style: { width: "auto" } });

  if (loading) {
    return <Loading />;
  }

  if (error) {
    return (
      <ErrorMessage
        onRefresh={requestDataCallback}
        message={translate.ErrorLoadingList}
      />
    );
  }
  const title = Switch(
    [entity === Entities.Client, TranslationService.translate.ClientFields],
    [entity === Entities.Invoice, TranslationService.translate.InvoiceFields],
    [entity === Entities.Payment, TranslationService.translate.PaymentFields]
  );

  return (
    <div className="main-layout-container mx-3">
      <div className="main-panel">
        <h4 className="mb-2">{title}</h4>
        <div className="card">
          <div className="card-body">
            <div className="d-flex mb-2">
              <div className="w-100 ps-0">
                <div className="col-auto ps-0">
                  <div className="actionButtonsHeader">
                    {!loading ? (
                      <ButtonTooltipIcon
                        withTitle
                        title={TranslationService.translate.New}
                        icon="fa-light fa-plus"
                        onClick={() => showAdditionalDefinitionsEditModal()}
                        isLink={false}
                      />
                    ) : null}
                  </div>
                </div>
              </div>
              <SearchInput onSearch={setValueSearch} autosearch={true} />
            </div>

            <div className="col-lg-12">
              <OrderableTable
                data={response}
                RowComponent={EntityFieldItem}
                headers={headers}
                getKey={(x) => ("Name" in x ? x.Name : x.field.toString())}
                onSort={onSort}
                reload={requestDataCallback}
                valueSearch={valueSearch}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export const ClientFieldConfiguration = () => (
  <EntityFieldConfiguration
    entity={Entities.Client}
    titleFieldSection={TranslationService.translate.NewFieldOf.replace(
      "{1}",
      TranslationService.translate.Clients
    )}
  />
);
export const InvoiceFieldConfiguration = () => (
  <EntityFieldConfiguration
    entity={Entities.Invoice}
    titleFieldSection={TranslationService.translate.NewFieldOf.replace(
      "{1}",
      TranslationService.translate.Invoices
    )}
  />
);
export const PaymentFieldConfiguration = () => (
  <EntityFieldConfiguration
    entity={Entities.Payment}
    titleFieldSection={TranslationService.translate.NewFieldOf.replace(
      "{1}",
      TranslationService.translate.Payments
    )}
  />
);
